import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { organizationService, Organization } from '@/firebase/organizationService';
import { userService } from '@/firebase/database';
import { usePreview } from './PreviewContext';
import { ensureSessionForCurrentOrg } from '@/services/sessionService';

interface OrganizationContextType {
  currentOrganization: Organization | null;
  organizations: Organization[];
  isLoading: boolean;
  setCurrentOrganization: (org: Organization | null) => void;
  loadOrganizations: () => Promise<void>;
  createOrganization: (name: string, industryType: string, description?: string) => Promise<{ success: boolean; error?: string; id?: string }>;
  refreshOrganization: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const OrganizationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { previewState, isPreviewMode } = usePreview();
  const [currentOrganization, setCurrentOrganizationState] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load user's organization from their profile and all organizations
  useEffect(() => {
    if (user?.id) {
      // If in preview mode, load the preview organization instead
      if (isPreviewMode && previewState) {
        loadPreviewOrganization();
      } else {
        loadUserOrganization();
        loadOrganizations(); // Also load all organizations user belongs to
      }
    } else {
      setCurrentOrganizationState(null);
      setOrganizations([]);
      setIsLoading(false);
    }
  }, [user?.id, isPreviewMode, previewState?.organizationId]);

  // GLBA: ensure session for current org when it's set or switched (Enterprise or event log for non-Enterprise)
  // Skip in preview mode: support user doesn't belong to the previewed org, so session creation would fail
  const lastSessionOrgIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!user?.id || !currentOrganization?.id || isPreviewMode) return;
    if (lastSessionOrgIdRef.current === currentOrganization.id) return;
    lastSessionOrgIdRef.current = currentOrganization.id;
    ensureSessionForCurrentOrg(currentOrganization.id).catch((err) => {
      console.warn('GLBA ensureSessionForCurrentOrg failed:', err);
    });
  }, [user?.id, currentOrganization?.id, isPreviewMode]);

  const loadPreviewOrganization = async () => {
    if (!previewState?.organizationId) {
      setIsLoading(false);
      return;
    }

    try {
      console.log('📥 Loading preview organization:', previewState.organizationId);
      const orgResult = await organizationService.getOrganization(previewState.organizationId);
      
      if (orgResult.success && orgResult.data) {
        console.log('✅ Preview organization loaded:', orgResult.data);
        setCurrentOrganizationState(orgResult.data);
      } else {
        console.log('⚠️ Preview organization not found:', previewState.organizationId);
        setCurrentOrganizationState(null);
      }
    } catch (error) {
      console.error('❌ Error loading preview organization:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadUserOrganization = async () => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    try {
      // First, check localStorage for the selected organization (from previous session)
      const savedOrgId = localStorage.getItem('currentOrganizationId');
      
      // Get user's organization data
      const userResult = await userService.getUser(user.id);
      
      if (userResult.success && userResult.data) {
        const userData = userResult.data;
        
        // Determine which organization to load:
        // 1. Priority: Saved organization from localStorage (if user belongs to it)
        // 2. Fallback: organizationId from user profile
        let organizationIdToLoad: string | null = null;
        
        // Get all organization IDs user belongs to
        const userOrgIds: string[] = [];
        if (userData.organizationIds && Array.isArray(userData.organizationIds)) {
          userOrgIds.push(...userData.organizationIds);
        }
        if (userData.organizationId && !userOrgIds.includes(userData.organizationId)) {
          userOrgIds.push(userData.organizationId);
        }
        
        // Check if saved organization ID is valid (user belongs to it)
        if (savedOrgId && userOrgIds.includes(savedOrgId)) {
          organizationIdToLoad = savedOrgId;
        } else if (userData.organizationId) {
          // Fall back to organizationId from profile
          organizationIdToLoad = userData.organizationId as string;
        }
        
        if (organizationIdToLoad) {
          // Only load if organization has changed
          if (currentOrganization?.id !== organizationIdToLoad) {
            // Load the organization
            const orgResult = await organizationService.getOrganization(organizationIdToLoad);
            
            if (orgResult.success && orgResult.data) {
              setCurrentOrganizationState(orgResult.data);
              // Update localStorage to match
              localStorage.setItem('currentOrganizationId', organizationIdToLoad);
            } else {
              // Organization not found - user might have been removed
              setCurrentOrganizationState(null);
              localStorage.removeItem('currentOrganizationId');
            }
          }
        } else {
          // User doesn't belong to an organization yet
          if (currentOrganization) {
            setCurrentOrganizationState(null);
          }
          localStorage.removeItem('currentOrganizationId');
        }
      }
    } catch (error) {
      console.error('❌ Error loading user organization:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadOrganizations = async () => {
    if (!user?.id) return;

    try {
      const orgMap = new Map<string, Organization>();
      
      // Get user data to access organizationIds array
      const userResult = await userService.getUser(user.id);
      if (userResult.success && userResult.data) {
        const userData = userResult.data;
        const userOrgIds: string[] = [];
        
        // Get organizationIds array (support multiple organizations)
        if (userData.organizationIds && Array.isArray(userData.organizationIds)) {
          userOrgIds.push(...userData.organizationIds);
        }
        
        // Backward compatibility: if user has single organizationId, add it
        if (userData.organizationId && !userOrgIds.includes(userData.organizationId)) {
          userOrgIds.push(userData.organizationId);
        }
        
        // Load all organizations user belongs to
        if (userOrgIds.length > 0) {
          const orgsResult = await organizationService.getOrganizationsByIds(userOrgIds);
          if (orgsResult.success && orgsResult.data) {
            orgsResult.data.forEach(org => {
              if (org.id && !orgMap.has(org.id)) {
                orgMap.set(org.id, org);
              }
            });
          }
        }
      }
      
      // Also load organizations owned by user (in case they're not in organizationIds yet)
      const ownedResult = await organizationService.getOrganizationsByOwner(user.id);
      if (ownedResult.success && ownedResult.data) {
        ownedResult.data.forEach(org => {
          if (org.id && !orgMap.has(org.id)) {
            orgMap.set(org.id, org);
      }
        });
      }
      
      // Convert Map to array to avoid duplicates
      const uniqueOrgs = Array.from(orgMap.values());
      setOrganizations(uniqueOrgs);
    } catch (error) {
      console.error('Error loading organizations:', error);
    }
  };

  const createOrganization = async (name: string, industryType: string, description?: string): Promise<{ success: boolean; error?: string; id?: string }> => {
    if (!user?.id) {
      return { success: false, error: 'User not authenticated' };
    }

    try {
      // Trim organization name before creating
      const trimmedName = name.trim();
      // Create organization
      const orgResult = await organizationService.createOrganization({
        name: trimmedName,
        industryType,
        description,
        ownerId: user.id
      });

      if (orgResult.success && orgResult.id) {
        // Get current user data to merge organizationRoles and organizationIds
        const currentUserResult = await userService.getUser(user.id);
        const currentUserData = currentUserResult.success ? currentUserResult.data : null;
        const existingOrgIds: string[] = currentUserData?.organizationIds && Array.isArray(currentUserData.organizationIds)
          ? [...currentUserData.organizationIds]
          : [];
        const existingOrgRoles: Record<string, { systemRole: string; ownerRoles: string[] }> =
          currentUserData?.organizationRoles && typeof currentUserData.organizationRoles === 'object'
            ? { ...currentUserData.organizationRoles }
            : {};

        // Merge new org into organizationIds
        const mergedOrgIds = existingOrgIds.includes(orgResult.id)
          ? existingOrgIds
          : [...existingOrgIds, orgResult.id];

        // Add organizationRoles for the new org (creator is admin)
        existingOrgRoles[orgResult.id] = { systemRole: 'admin', ownerRoles: [] };

        // Update user with organizationId, organizationIds, and organizationRoles (not just legacy role)
        await userService.updateUser(user.id, {
          organizationId: orgResult.id,
          organizationIds: mergedOrgIds,
          organizationRoles: existingOrgRoles,
          role: 'admin' // Keep for backward compatibility
        });

        // Load the new organization
        const loadedOrg = await organizationService.getOrganization(orgResult.id);
        if (loadedOrg.success && loadedOrg.data) {
          setCurrentOrganizationState(loadedOrg.data);
        }

        return { success: true, id: orgResult.id };
      } else {
        return { success: false, error: orgResult.error || 'Failed to create organization' };
      }
    } catch (error: any) {
      console.error('Error creating organization:', error);
      return { success: false, error: error.message };
    }
  };

  const setCurrentOrganization = async (org: Organization | null) => {
    if (!user?.id) return;

    setCurrentOrganizationState(org);
    
    // Store selected organization in localStorage for persistence across page reloads
    if (org?.id) {
      localStorage.setItem('currentOrganizationId', org.id);
    } else {
      localStorage.removeItem('currentOrganizationId');
    }
    
    // Update organizationIds array while preserving existing organizationId and locationIds
    if (org?.id) {
      // First, get current user data to preserve locationIds and check existing organizationIds
      const currentUserResult = await userService.getUser(user.id);
      const currentUserData = currentUserResult.success ? currentUserResult.data : null;
      const currentLocationIds = currentUserData?.locationIds 
        ? currentUserData.locationIds 
        : undefined;
      
      // Get current organizationIds array (or create empty array if it doesn't exist)
      const currentOrgIds: string[] = currentUserData?.organizationIds && Array.isArray(currentUserData.organizationIds)
        ? [...currentUserData.organizationIds]
        : [];
      
      // If organizationId exists but not in organizationIds array, add it for backward compatibility
      if (currentUserData?.organizationId && !currentOrgIds.includes(currentUserData.organizationId)) {
        currentOrgIds.push(currentUserData.organizationId);
      }
      
      // Add new organizationId to array if it's not already there
      if (!currentOrgIds.includes(org.id)) {
        currentOrgIds.push(org.id);
      }
      
      // Prepare update data - update organizationIds array but keep organizationId unchanged
      // Note: updatedAt will be set automatically by updateUser using serverTimestamp()
      const updateData: any = {
        organizationIds: currentOrgIds
      };
      
      // Only preserve locationIds if they exist (don't overwrite with undefined)
      if (currentLocationIds && Array.isArray(currentLocationIds) && currentLocationIds.length > 0) {
        updateData.locationIds = currentLocationIds;
      }
      
      await userService.updateUser(user.id, updateData);
    }
  };

  const refreshOrganization = async () => {
    if (currentOrganization?.id) {
      const result = await organizationService.getOrganization(currentOrganization.id);
      if (result.success && result.data) {
        setCurrentOrganizationState(result.data);
      }
    }
  };

  const value: OrganizationContextType = {
    currentOrganization,
    organizations,
    isLoading,
    setCurrentOrganization,
    loadOrganizations,
    createOrganization,
    refreshOrganization
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    // Return a default context instead of throwing to prevent crashes during initialization
    console.warn('useOrganization called outside OrganizationProvider, returning default context');
    return {
      currentOrganization: null,
      organizations: [],
      isLoading: true,
      setCurrentOrganization: async () => {},
      loadOrganizations: async () => {},
      createOrganization: async () => ({ success: false, error: 'Provider not available' }),
      refreshOrganization: async () => {},
    };
  }
  return context;
};
